import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/Table';
import {
  useRenovacoes, useRankingOrgaos, useGrandesContratos,
  useAtasEsgotando,
  useFichaOrgao, useWatchlist,
  adicionarWatchlist, removerWatchlist,
  type FichaMercado, type WatchlistItem,
} from '@/hooks/useMercadoContratos';
import { RaioXMercado } from '@/components/mercado/RaioXMercado';
import { ContratosMercado } from '@/components/mercado/ContratosMercado';
import { PrecosMercado } from '@/components/mercado/PrecosMercado';
import { CapagMercado } from '@/components/mercado/CapagMercado';

type Aba = 'raiox' | 'contratos' | 'precos' | 'capag' | 'renovacoes' | 'orgaos' | 'atas' | 'grandes';

const ABAS: { id: Aba; label: string }[] = [
  { id: 'raiox', label: 'Raio-X de Mercado' },
  { id: 'contratos', label: 'Contratos' },
  { id: 'precos', label: 'Consultar Preços' },
  { id: 'capag', label: 'CAPAG Municípios' },
  { id: 'renovacoes', label: 'Radar de Renovação' },
  { id: 'orgaos', label: 'Inteligência por Órgão' },
  { id: 'atas', label: 'Atas Esgotando' },
  { id: 'grandes', label: 'Grandes Contratos' },
];

const brl = (v: number | string | null | undefined) =>
  v == null || v === ''
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(Number(v));

const dataBR = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(`${String(v).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

function EstadoVazio({ texto }: { texto: string }) {
  return <div className="py-10 text-center text-sm text-ds-text-secondary">{texto}</div>;
}

function Carregando() {
  return <div className="py-10 text-center text-sm text-ds-text-secondary">Carregando…</div>;
}

function ErroConsulta() {
  return <div className="py-10 text-center text-sm text-ds-danger">Falha ao carregar os dados de mercado.</div>;
}

/** Input de UF (2 letras) + controle numérico, compartilhado pelos filtros das abas. */
function FiltrosLinha({ children }: { children: React.ReactNode }) {
  return <div className="mb-4 flex flex-wrap items-end gap-3">{children}</div>;
}

function CampoUf({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ds-text-secondary">
      UF
      <input
        value={value}
        maxLength={2}
        placeholder="Todas"
        onChange={(e) => onChange(e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase())}
        className="w-20 rounded-md border border-ds-border bg-ds-surface px-2 py-1 text-sm text-ds-text-primary"
        aria-label="UF"
      />
    </label>
  );
}

function AbaRenovacoes({ portal }: { portal?: boolean }) {
  const [uf, setUf] = useState('');
  const [janelaDias, setJanelaDias] = useState(90);
  const [termos, setTermos] = useState('');
  const { data, isLoading, isError } = useRenovacoes({ uf: uf || undefined, termos: termos || undefined, janelaDias }, portal);

  return (
    <div>
      <FiltrosLinha>
        <CampoUf value={uf} onChange={setUf} />
        <label className="flex flex-col gap-1 text-xs text-ds-text-secondary">
          Vencendo em até
          <select
            value={janelaDias}
            onChange={(e) => setJanelaDias(Number(e.target.value))}
            className="rounded-md border border-ds-border bg-ds-surface px-2 py-1 text-sm text-ds-text-primary"
          >
            {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} dias</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1 text-xs text-ds-text-secondary">
          Palavra-chave no objeto (opcional)
          <input
            value={termos}
            onChange={(e) => setTermos(e.target.value)}
            placeholder="ex.: transporte, limpeza"
            className="min-w-[160px] rounded-md border border-ds-border bg-ds-surface px-2 py-1 text-sm text-ds-text-primary"
          />
        </label>
      </FiltrosLinha>

      {isLoading ? <Carregando /> : isError ? <ErroConsulta /> : !data || data.length === 0 ? (
        <EstadoVazio texto="Nenhum contrato vencendo nessa janela. A base é sincronizada dos contratos do PNCP." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Objeto</TableHead>
              <TableHead>Órgão</TableHead>
              <TableHead>UF</TableHead>
              <TableHead>Fornecedor atual</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Vence em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((c) => {
              const dias = c.dias_para_fim;
              const variante = dias == null ? 'default' : dias <= 30 ? 'danger' : dias <= 60 ? 'warning' : 'info';
              return (
                <TableRow key={c.numero_controle_pncp}>
                  <TableCell className="max-w-[320px] truncate" title={c.objeto_contrato || ''}>{c.objeto_contrato || '—'}</TableCell>
                  <TableCell className="max-w-[200px] truncate" title={c.orgao_razao_social || ''}>{c.orgao_razao_social || '—'}</TableCell>
                  <TableCell>{c.unidade_uf || '—'}</TableCell>
                  <TableCell className="max-w-[200px] truncate" title={c.fornecedor_nome || ''}>{c.fornecedor_nome || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{brl(c.valor_global)}</TableCell>
                  <TableCell>
                    <Badge variant={variante}>{dataBR(c.data_vigencia_fim)}{dias != null ? ` · ${dias}d` : ''}</Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function SeletorPeriodo({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ds-text-secondary">
      Período
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="rounded-md border border-ds-border bg-ds-surface px-2 py-1 text-sm text-ds-text-primary"
      >
        {[6, 12, 24, 36].map((m) => <option key={m} value={m}>{m} meses</option>)}
      </select>
    </label>
  );
}

function CardResumo({ titulo, valor, detalhe }: { titulo: string; valor: string; detalhe?: string }) {
  return (
    <div className="rounded-lg border border-ds-border bg-ds-surface2 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-ds-text-secondary">{titulo}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-ds-text-primary">{valor}</p>
      {detalhe ? <p className="mt-1 text-xs text-ds-text-secondary">{detalhe}</p> : null}
    </div>
  );
}

function FichaPainel({
  ficha,
  tipo,
  onMonitorar,
  monitorando,
}: {
  ficha: FichaMercado;
  tipo: 'orgao' | 'fornecedor';
  onMonitorar: () => void;
  monitorando: boolean;
}) {
  return (
    <div className="mb-6 space-y-4 rounded-lg border border-ds-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-ds-text-primary">{ficha.nome || 'Sem nome na base'}</h3>
          <p className="text-sm tabular-nums text-ds-text-secondary">CNPJ/CPF: {ficha.cnpj}</p>
        </div>
        <button
          type="button"
          onClick={onMonitorar}
          disabled={monitorando}
          className="rounded-md bg-ds-brand-solid px-3 py-1.5 text-sm font-medium text-ds-on-brand disabled:opacity-60"
        >
          {monitorando ? 'Monitorando…' : 'Monitorar'}
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <CardResumo titulo="Contratos no período" valor={String(ficha.total_contratos)} />
        <CardResumo titulo="Valor total" valor={brl(ficha.valor_total)} />
      </div>
      {ficha.onde.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ds-text-primary">Onde atua</h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>UF</TableHead>
                <TableHead>Município</TableHead>
                <TableHead className="text-right">Contratos</TableHead>
                <TableHead className="text-right">Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ficha.onde.map((o) => (
                <TableRow key={`${o.uf}-${o.municipio}`}>
                  <TableCell>{o.uf || '—'}</TableCell>
                  <TableCell>{o.municipio || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{o.total_contratos}</TableCell>
                  <TableCell className="text-right tabular-nums">{brl(o.valor_total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {ficha.contratos.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ds-text-primary">Contratos recentes</h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Objeto</TableHead>
                <TableHead>{tipo === 'orgao' ? 'Fornecedor' : 'Órgão'}</TableHead>
                <TableHead>UF</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Assinatura</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ficha.contratos.map((c) => (
                <TableRow key={c.numero_controle_pncp}>
                  <TableCell className="max-w-[280px] truncate" title={c.objeto_contrato || ''}>{c.objeto_contrato || '—'}</TableCell>
                  <TableCell className="max-w-[180px] truncate">
                    {tipo === 'orgao' ? (c.fornecedor_nome || '—') : (c.orgao_razao_social || '—')}
                  </TableCell>
                  <TableCell>{c.unidade_uf || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{brl(c.valor_global)}</TableCell>
                  <TableCell>{dataBR(c.data_assinatura)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {ficha.total_contratos === 0 && (
        <EstadoVazio texto="Nenhum contrato deste CNPJ na base local do PNCP no período." />
      )}
    </div>
  );
}

function ListaMonitorados({
  items,
  onAbrir,
  onRemover,
}: {
  items: WatchlistItem[];
  onAbrir: (cnpj: string) => void;
  onRemover: (id: number) => void;
}) {
  if (!items.length) return null;
  return (
    <section className="mb-6">
      <h3 className="mb-2 text-sm font-semibold text-ds-text-primary">Monitorados</h3>
      <ul className="flex flex-wrap gap-2">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex items-center gap-2 rounded-full border border-ds-border bg-ds-surface2 px-3 py-1 text-sm"
          >
            <button type="button" className="text-ds-text-primary hover:underline" onClick={() => onAbrir(item.cnpj)}>
              {item.rotulo || item.cnpj}
            </button>
            <button
              type="button"
              className="text-xs text-ds-text-secondary hover:text-ds-danger"
              onClick={() => onRemover(item.id)}
              aria-label={`Remover ${item.rotulo || item.cnpj}`}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AbaOrgaos({ portal }: { portal?: boolean }) {
  const qc = useQueryClient();
  const [uf, setUf] = useState('');
  const [periodoMeses, setPeriodoMeses] = useState(12);
  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [monitorando, setMonitorando] = useState(false);
  const { data, isLoading, isError } = useRankingOrgaos({
    uf: uf || undefined,
    periodoMeses,
  }, portal);
  const ficha = useFichaOrgao({ cnpj, periodoMeses }, portal);
  const watch = useWatchlist('orgao', portal);

  const buscar = (valor?: string) => {
    const next = (valor ?? cnpjInput).trim();
    setCnpj(next);
  };

  const monitorar = async () => {
    if (!ficha.data?.cnpj) return;
    setMonitorando(true);
    try {
      await adicionarWatchlist({ tipo: 'orgao', cnpj: ficha.data.cnpj, rotulo: ficha.data.nome || undefined }, portal);
      await qc.invalidateQueries({ queryKey: ['mercado-watchlist'] });
    } finally {
      setMonitorando(false);
    }
  };

  const remover = async (id: number) => {
    await removerWatchlist(id, portal);
    await qc.invalidateQueries({ queryKey: ['mercado-watchlist'] });
  };

  return (
    <div>
      <FiltrosLinha>
        <CampoUf value={uf} onChange={setUf} />
        <SeletorPeriodo value={periodoMeses} onChange={setPeriodoMeses} />
        <form
          className="flex flex-1 flex-wrap items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); buscar(); }}
        >
          <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-xs text-ds-text-secondary">
            Buscar CNPJ do órgão
            <input
              value={cnpjInput}
              onChange={(e) => setCnpjInput(e.target.value)}
              placeholder="00.000.000/0001-00"
              className="rounded-md border border-ds-border bg-ds-surface px-2 py-1 text-sm text-ds-text-primary"
            />
          </label>
          <button type="submit" className="rounded-md bg-ds-brand-solid px-3 py-1.5 text-sm font-medium text-ds-on-brand">
            Pesquisar
          </button>
        </form>
      </FiltrosLinha>

      <ListaMonitorados
        items={watch.data || []}
        onAbrir={(ni) => { setCnpjInput(ni); buscar(ni); }}
        onRemover={remover}
      />

      {cnpj && (
        ficha.isLoading ? <Carregando /> : ficha.isError ? <ErroConsulta /> : ficha.data ? (
          <FichaPainel
            ficha={ficha.data}
            tipo="orgao"
            onMonitorar={monitorar}
            monitorando={monitorando}
          />
        ) : null
      )}

      <h3 className="mb-2 text-sm font-semibold text-ds-text-primary">Maiores compradores</h3>
      {isLoading ? <Carregando /> : isError ? <ErroConsulta /> : !data || data.length === 0 ? (
        <EstadoVazio texto="Sem contratos no período. A base é sincronizada do PNCP." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Órgão</TableHead>
              <TableHead>UF</TableHead>
              <TableHead className="text-right">Contratos</TableHead>
              <TableHead className="text-right">Valor total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((o) => (
              <TableRow key={o.orgao_cnpj}>
                <TableCell className="max-w-[360px] truncate" title={o.orgao_razao_social || ''}>
                  <button
                    type="button"
                    className="text-left hover:underline"
                    onClick={() => { setCnpjInput(o.orgao_cnpj); buscar(o.orgao_cnpj); }}
                  >
                    {o.orgao_razao_social || o.orgao_cnpj}
                  </button>
                </TableCell>
                <TableCell>{o.unidade_uf || '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{o.total_contratos}</TableCell>
                <TableCell className="text-right tabular-nums">{brl(o.valor_total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function AbaGrandes({ portal }: { portal?: boolean }) {
  const [uf, setUf] = useState('');
  const { data, isLoading, isError } = useGrandesContratos({ uf: uf || undefined }, portal);

  return (
    <div>
      <FiltrosLinha>
        <CampoUf value={uf} onChange={setUf} />
      </FiltrosLinha>
      {isLoading ? <Carregando /> : isError ? <ErroConsulta /> : !data || data.length === 0 ? (
        <EstadoVazio texto="Sem contratos. A base é sincronizada do PNCP." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Objeto</TableHead>
              <TableHead>Órgão</TableHead>
              <TableHead>UF</TableHead>
              <TableHead>Fornecedor</TableHead>
              <TableHead className="text-right">Valor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((c) => (
              <TableRow key={c.numero_controle_pncp}>
                <TableCell className="max-w-[320px] truncate" title={c.objeto_contrato || ''}>{c.objeto_contrato || '—'}</TableCell>
                <TableCell className="max-w-[200px] truncate" title={c.orgao_razao_social || ''}>{c.orgao_razao_social || '—'}</TableCell>
                <TableCell>{c.unidade_uf || '—'}</TableCell>
                <TableCell className="max-w-[200px] truncate" title={c.fornecedor_nome || ''}>{c.fornecedor_nome || '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{brl(c.valor_global)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function AbaAtas({ portal }: { portal?: boolean }) {
  const [janelaDias, setJanelaDias] = useState(90);
  const [termos, setTermos] = useState('');
  const { data, isLoading, isError } = useAtasEsgotando({ termos: termos || undefined, janelaDias }, portal);

  return (
    <div>
      <FiltrosLinha>
        <label className="flex flex-col gap-1 text-xs text-ds-text-secondary">
          Vigência terminando em até
          <select
            value={janelaDias}
            onChange={(e) => setJanelaDias(Number(e.target.value))}
            className="rounded-md border border-ds-border bg-ds-surface px-2 py-1 text-sm text-ds-text-primary"
          >
            {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} dias</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1 text-xs text-ds-text-secondary">
          Palavra-chave no objeto (opcional)
          <input
            value={termos}
            onChange={(e) => setTermos(e.target.value)}
            placeholder="ex.: medicamento, combustível"
            className="min-w-[160px] rounded-md border border-ds-border bg-ds-surface px-2 py-1 text-sm text-ds-text-primary"
          />
        </label>
      </FiltrosLinha>

      {isLoading ? <Carregando /> : isError ? <ErroConsulta /> : !data || data.length === 0 ? (
        <EstadoVazio texto="Nenhuma ata esgotando nessa janela. A base é sincronizada das atas do PNCP." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Objeto</TableHead>
              <TableHead>Órgão</TableHead>
              <TableHead>Adesão</TableHead>
              <TableHead>Esgota em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((a) => {
              const dias = a.dias_para_fim;
              const variante = dias == null ? 'default' : dias <= 30 ? 'danger' : dias <= 60 ? 'warning' : 'info';
              return (
                <TableRow key={a.numero_controle_pncp_ata}>
                  <TableCell className="max-w-[340px] truncate" title={a.objeto_contratacao || ''}>{a.objeto_contratacao || '—'}</TableCell>
                  <TableCell className="max-w-[220px] truncate" title={a.nome_orgao || ''}>{a.nome_orgao || '—'}</TableCell>
                  <TableCell>{a.possibilidade_adesao ? <Badge variant="success">Permite</Badge> : <Badge variant="default">—</Badge>}</TableCell>
                  <TableCell><Badge variant={variante}>{dataBR(a.vigencia_fim)}{dias != null ? ` · ${dias}d` : ''}</Badge></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/**
 * Inteligência de Mercado — Contratos homologados do PNCP (IMC3/IMC4).
 * Tela compartilhada entre o app principal (staff) e o portal da empresa.
 */
export function MercadoInteligencia({ portal }: { portal?: boolean } = {}) {
  const [aba, setAba] = useState<Aba>('raiox');

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-ds-text-primary">Inteligência de Mercado</h1>
        <p className="text-sm text-ds-text-secondary">
          Contratos homologados do PNCP — descubra contratos vencendo, maiores compradores e fornecedores.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="sr-only">Abas</CardTitle>
          <nav className="flex flex-wrap gap-2" aria-label="Seções de inteligência de mercado">
            {ABAS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setAba(a.id)}
                aria-current={aba === a.id ? 'page' : undefined}
                className={
                  aba === a.id
                    ? 'rounded-md bg-ds-brand-solid px-3 py-1.5 text-sm font-medium text-ds-on-brand'
                    : 'rounded-md border border-ds-border px-3 py-1.5 text-sm text-ds-text-secondary hover:bg-ds-surface2'
                }
              >
                {a.label}
              </button>
            ))}
          </nav>
        </CardHeader>
        <CardContent>
          {aba === 'raiox' && <RaioXMercado portal={portal} />}
          {aba === 'contratos' && <ContratosMercado portal={portal} />}
          {aba === 'precos' && <PrecosMercado portal={portal} />}
          {aba === 'capag' && <CapagMercado portal={portal} />}
          {aba === 'renovacoes' && <AbaRenovacoes portal={portal} />}
          {aba === 'orgaos' && <AbaOrgaos portal={portal} />}
          {aba === 'atas' && <AbaAtas portal={portal} />}
          {aba === 'grandes' && <AbaGrandes portal={portal} />}
        </CardContent>
      </Card>
    </div>
  );
}

export default MercadoInteligencia;
